import { isPlainObject, safelyParseJSONString } from "./jsonUtils";

/**
 * Removes the Byte Order Mark (BOM) from the start of a string if it exists.
 */
export function removeBOM(content: string): string {
  return content.startsWith("\uFEFF") ? content.slice(1) : content;
}

/**
 * Parses a single CSV row handling quoted fields and escaped quotes.
 *
 * @param row The raw CSV row string.
 * @returns An array of field strings.
 */
export function parseCSVRow(row: string): string[] {
  const fields: string[] = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      if (inQuotes && i + 1 < row.length && row[i + 1] === '"') {
        // Escaped quote: "" -> "
        currentField += '"';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      fields.push(currentField.trim());
      currentField = "";
    } else {
      currentField += char;
    }
  }

  fields.push(currentField.trim());
  return fields;
}

/**
 * Finds the index of the character where a complete CSV row ends.
 * Returns -1 if no complete row end is found.
 */
export function findCompleteCSVRowEnd(content: string): number {
  let inQuotes = false;
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    if (char === '"') {
      if (i + 1 < content.length && content[i + 1] === '"') {
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "\n" && !inQuotes) {
      return i;
    } else if (char === "\r" && !inQuotes) {
      if (i + 1 < content.length && content[i + 1] === "\n") {
        return i + 1;
      }
      return i;
    }
  }
  return -1;
}

/**
 * Parses a CSV file by streaming it to avoid loading the entire content into memory.
 * Returns the column names, a preview of rows, and the total row count.
 *
 * It also identifies "collapsible" columns - those where every value in the preview
 * rows is a valid JSON object.
 */
export async function parseCSVFile(
  file: File,
  maxPreviewRows = 100
): Promise<{
  columns: string[];
  previewRows: string[][];
  totalRowCount: number;
  collapsibleColumns: string[];
}> {
  const reader = file.stream().getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let columns: string[] = [];
  const previewRows: string[][] = [];
  let totalRowCount = 0;
  let isFirstRow = true;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done && !buffer) break;

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      // Handle BOM at start of file
      if (isFirstRow && buffer.length > 0) {
        buffer = removeBOM(buffer);
      }

      while (true) {
        const rowEnd = findCompleteCSVRowEnd(buffer);
        if (rowEnd === -1) break;

        const rowStr = buffer.slice(0, rowEnd).trim();
        buffer = buffer.slice(rowEnd + 1);

        if (rowStr) {
          const parsedRow = parseCSVRow(rowStr);
          if (isFirstRow) {
            columns = parsedRow;
            isFirstRow = false;
          } else {
            if (previewRows.length < maxPreviewRows) {
              previewRows.push(parsedRow);
            }
            totalRowCount++;
          }
        }
      }

      if (done) {
        // Handle last row if no trailing newline
        const finalRow = buffer.trim();
        if (finalRow) {
          const parsedRow = parseCSVRow(finalRow);
          if (isFirstRow) {
            columns = parsedRow;
          } else {
            if (previewRows.length < maxPreviewRows) {
              previewRows.push(parsedRow);
            }
            totalRowCount++;
          }
        }
        break;
      }
    }

    if (columns.length === 0) {
      throw new Error("CSV file is empty");
    }

    // Identify collapsible columns (those that consistently contain JSON objects)
    const collapsibleColumns: string[] = [];
    if (previewRows.length > 0) {
      columns.forEach((col, colIdx) => {
        const isCollapsible = previewRows.every((row) => {
          const cell = row[colIdx];
          if (!cell) return false;
          const parsed = safelyParseJSONString(cell);
          return isPlainObject(parsed);
        });
        if (isCollapsible) {
          collapsibleColumns.push(col);
        }
      });
    }

    return { columns, previewRows, totalRowCount, collapsibleColumns };
  } finally {
    reader.cancel();
  }
}

export function downloadCSV(data: any[], filename: string) {
  if (!data || !data.length) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((fieldName) => {
          const value = row[fieldName];
          let cell = value === null || value === undefined ? "" : String(value);

          // Sanitize formulas to prevent CSV Injection in Excel
          if (/^[=+\-@]/.test(cell)) {
            cell = "'" + cell;
          }

          // Escape quotes and wrap in quotes if contains comma/newline/quote
          if (cell.includes(",") || cell.includes("\n") || cell.includes('"')) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
